import React, { useEffect, useState, useRef } from "react";
import { Users, Eye, CreditCard, FolderKanban, Save, Loader2, RefreshCw, Upload, Trash2, CheckCircle2, Tag, Plus, Globe, Coins, MessageSquare, Mail, Star, Send, Download, ShieldCheck, Puzzle, Wallet, Activity, BookOpen } from "lucide-react";
import { saveBlob } from "@/lib/download";
import api, { formatError } from "@/lib/api";
import { AdminCustomers } from "@/components/AdminCustomers";
import { AdminFunnel } from "@/components/AdminFunnel";
import { BrandAssets } from "@/components/BrandAssets";
import { WhatsAppDiagnostics } from "@/components/WhatsAppDiagnostics";
import { WhatsAppInbox } from "@/components/WhatsAppInbox";
import { Escalations } from "@/components/whatsapp/Escalations";
import { WhatsAppProfile } from "@/components/WhatsAppProfile";
import { WhatsAppTenants } from "@/components/WhatsAppTenants";
import { ProductRatingsAdmin } from "@/components/ProductRatingsAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AdminFinance } from "@/components/AdminFinance";
import { VatSettingsCard } from "@/components/VatSettingsCard";
import { AdminStudy } from "@/components/AdminStudy";
import { AdminCredits } from "@/components/AdminCredits";
import { AdminAddons } from "@/components/AdminAddons";
import { MetaReviewerCard } from "@/components/MetaReviewerCard";
import { AdminRules } from "@/components/AdminRules";
import { AdminMusic } from "@/components/AdminMusic";
import { AdminLoad } from "@/components/AdminLoad";
import { AdminCostLab } from "@/components/AdminCostLab";
import { VoiceLab } from "@/components/VoiceLab";
import { AdminOpsAlerts } from "@/components/AdminOpsAlerts";
import { AdminSection } from "@/components/AdminSection";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const Card = ({ icon: Icon, label, value, color, testid }) => (
  <div className="glass rounded-2xl p-5 flex items-center gap-4" data-testid={testid}>
    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, color }}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </div>
);

const StatBox = ({ label, value, accent }) => (
  <div className="border border-border rounded-xl px-4 py-3">
    <div className={`text-xl font-extrabold ${accent ? "text-accent" : ""}`}>{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function AdminOverview() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <AdminOnly />;
  return <AdminOverviewInner />;
}

function AdminOnly() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="max-w-md mx-auto glass rounded-2xl p-8 text-center space-y-4 mt-10" data-testid="admin-only-notice">
      <h2 className="text-xl font-bold">هذه الصفحة للمشرف فقط</h2>
      <p className="text-sm text-muted-foreground">أنت داخل بحساب عادي. سجّل الخروج ثم ادخل بحساب المشرف admin@rawaj.sa.</p>
      <Button onClick={async () => { await logout(); navigate("/login"); }} className="w-full" data-testid="admin-only-logout">تسجيل الخروج والدخول كمشرف</Button>
    </div>
  );
}

function AdminOverviewInner() {
  const [data, setData] = useState(null);
  const [vb, setVb] = useState(0);
  const [ub, setUb] = useState(0);
  const [saving, setSaving] = useState(false);
  const [gv, setGv] = useState(null);
  const [busy, setBusy] = useState(null); // stage number currently uploading/deleting
  const fileRefs = useRef({});

  // Coupons
  const [coupons, setCoupons] = useState([]);
  const [cCode, setCCode] = useState("");
  const [cPct, setCPct] = useState(30);
  const [cMax, setCMax] = useState("");
  const [cExp, setCExp] = useState("");
  const [cSaving, setCSaving] = useState(false);

  const loadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  const [usage, setUsage] = useState(null);
  const loadUsage = () => api.get("/admin/usage").then((r) => setUsage(r.data)).catch(() => {});
  const [feedback, setFeedback] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState(null);
  const [reviews, setReviews] = useState([]);
  const loadFeedback = () => api.get("/admin/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { loadCoupons(); loadUsage(); loadFeedback(); loadReviews(); }, []);

  const sendReply = async (f) => {
    const txt = (replyText[f.id] || "").trim();
    if (!txt) { toast.error("اكتب نص الرد"); return; }
    setReplying(f.id);
    try {
      await api.post(`/admin/feedback/${f.id}/reply`, { reply: txt });
      toast.success("تم إرسال الرد إلى بريد العميل ✅");
      setReplyText((p) => ({ ...p, [f.id]: "" }));
      loadFeedback();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReplying(null); }
  };

  const deleteFeedback = async (f) => {
    if (!window.confirm("حذف هذه الرسالة؟")) return;
    try { await api.delete(`/admin/feedback/${f.id}`); loadFeedback(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deleteReview = async (r) => {
    if (!window.confirm("حذف هذا التقييم؟")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("تم الحذف"); loadReviews(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const KIND_LABEL = { suggestion: "اقتراح", recommendation: "توصية", complaint: "شكوى" };

  const renewCoupon = async (c, days = 30) => {
    const exp = new Date(Date.now() + days * 86400000).toISOString();
    try { await api.patch(`/admin/coupons/${c.id}`, { expires_at: exp, active: true }); toast.success(`تم تجديد ${c.code} +${days} يوم`); loadCoupons(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setCouponExpiry = async (c, dateStr) => {
    const exp = dateStr ? new Date(dateStr).toISOString() : null;
    try { await api.patch(`/admin/coupons/${c.id}`, { expires_at: exp }); toast.success("تم تحديث تاريخ الانتهاء"); loadCoupons(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const togglePromo = async (c) => {
    try { await api.patch(`/admin/coupons/${c.id}`, { promo: !c.promo }); loadCoupons(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const createCoupon = async () => {
    const code = cCode.trim().toUpperCase();
    if (!code) { toast.error("أدخل كود الكوبون"); return; }
    setCSaving(true);
    try {
      await api.post("/admin/coupons", {
        code, discount_percent: Number(cPct) || 30,
        max_uses: cMax ? Number(cMax) : null,
        expires_at: cExp ? new Date(cExp).toISOString() : null,
        active: true,
      });
      toast.success("تم إنشاء الكوبون");
      setCCode(""); setCPct(30); setCMax(""); setCExp("");
      loadCoupons();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setCSaving(false); }
  };

  const toggleCoupon = async (c) => {
    try { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); loadCoupons(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deleteCoupon = async (c) => {
    if (!window.confirm(`حذف الكوبون ${c.code}؟`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("تم الحذف"); loadCoupons(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const loadGv = () => api.get("/admin/guide-videos/list").then((r) => setGv(r.data)).catch(() => {});
  useEffect(() => { loadGv(); }, []);

  const pickFile = (n) => fileRefs.current[n]?.click();

  const uploadVideo = async (n, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("الرجاء اختيار ملف فيديو"); return; }
    setBusy(n);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/guide-videos/upload/${n}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع الفيديو وسيظهر في صفحة دليل الاستخدام");
      loadGv();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(null); if (fileRefs.current[n]) fileRefs.current[n].value = ""; }
  };

  const deleteVideo = async (n) => {
    if (!window.confirm("حذف فيديو هذه المرحلة؟")) return;
    setBusy(n);
    try {
      await api.delete(`/admin/guide-videos/${n}`);
      toast.success("تم حذف الفيديو");
      loadGv();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const load = () => api.get("/admin/overview").then((r) => {
    setData(r.data); setVb(r.data.visitors_base); setUb(r.data.users_base);
  }).catch((e) => toast.error(formatError(e.response?.data?.detail)));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/overview", { visitors_base: Number(vb) || 0, users_base: Number(ub) || 0 });
      setData(d); toast.success("تم تحديث العدّادات المعروضة");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">لوحة الإدارة</h1>
          <p className="text-muted-foreground mt-1 text-sm">متابعة الزوار والاشتراكات والتحكم في العدّادات المعروضة على الواجهة.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2 border-border" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /> تحديث</Button>
      </div>

      <AdminOpsAlerts />

      <AdminSection id="rules" title="قوانين التوليد والمخرج" desc="قوانين رواج الذكية، اختبار التوليد، الشخصيات الصوتية، الموسيقى" icon={ShieldCheck} defaultOpen={true}>
      <AdminRules />
      <VoiceLab />
      <AdminMusic />
      </AdminSection>

      <AdminSection id="whatsapp" title="واتساب — صندوق الوارد وبوت حمد" desc="محادثات العملاء على واتساب، الرد اليدوي، وإيقاف/تشغيل الرد التلقائي" icon={MessageSquare} defaultOpen={false}>
      <WhatsAppInbox />
      <div className="rounded-2xl border border-border p-4 space-y-3"><h3 className="font-bold text-sm">التصعيدات (كل البوتات) — أجب مرة واحدة ويتعلّم المساعد</h3><Escalations admin /></div>
      <WhatsAppTenants />
      <WhatsAppProfile />
      </AdminSection>

      <AdminSection id="addons" title="الإضافات والخدمات والدليل" desc="سوبر رواج والإضافات، فيديوهات دليل الاستخدام" icon={Puzzle} defaultOpen={false}>
      <AdminAddons />
      <MetaReviewerCard />
      <WhatsAppDiagnostics />
      <BrandAssets />
      <div className="glass rounded-2xl p-6" data-testid="guide-videos-admin">
        <h2 className="text-lg font-bold mb-1">فيديوهات دليل الاستخدام الإرشادية</h2>
        <p className="text-xs text-muted-foreground mb-5">ارفع فيديو تعريفي يدوياً لكل مرحلة من مراحل الدليل. تظهر الفيديوهات مباشرة في صفحة دليل الاستخدام. (الحد الأقصى 60 ميجابايت للفيديو)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {(gv || []).map((s) => (
            <div key={s.n} className="border border-border rounded-xl p-4 flex flex-col gap-3" data-testid={`guide-stage-${s.n}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-sm flex items-center gap-2">
                    {s.has_video && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    {s.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${s.has_video ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                  {s.has_video ? "تم الرفع" : "لا يوجد فيديو"}
                </span>
              </div>
              {s.has_video && s.video_id && (
                <video
                  className="w-full aspect-video rounded-lg border border-border bg-black"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/guide-video/${s.video_id}`}
                  controls preload="metadata" data-testid={`guide-stage-preview-${s.n}`}
                />
              )}
              <input
                type="file" accept="video/*" className="hidden"
                ref={(el) => (fileRefs.current[s.n] = el)}
                onChange={(e) => uploadVideo(s.n, e.target.files)}
                data-testid={`guide-stage-file-${s.n}`}
              />
              <div className="flex gap-2 mt-auto">
                <Button
                  size="sm" variant="outline" className="gap-2 flex-1 border-border"
                  onClick={() => pickFile(s.n)} disabled={busy === s.n}
                  data-testid={`guide-stage-upload-${s.n}`}
                >
                  {busy === s.n ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {s.has_video ? "استبدال الفيديو" : "رفع فيديو"}
                </Button>
                {s.has_video && (
                  <Button
                    size="sm" variant="outline" className="gap-2 border-border"
                    onClick={async () => { try { const r = await api.get(`/guide-video/${s.video_id}`, { responseType: "blob", timeout: 300000 }); await saveBlob(r.data, `rawaj-guide-${s.n}.mp4`); } catch (e) { toast.error("تعذّر تنزيل الفيديو"); } }}
                    data-testid={`guide-stage-download-${s.n}`}
                  >
                    <Download className="w-4 h-4" /> تنزيل
                  </Button>
                )}
                {s.has_video && (
                  <Button
                    size="sm" variant="outline" className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
                    onClick={() => deleteVideo(s.n)} disabled={busy === s.n}
                    data-testid={`guide-stage-delete-${s.n}`}
                  >
                    <Trash2 className="w-4 h-4" /> حذف
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      </AdminSection>

      <AdminSection id="customers" title="العملاء والزوار والعدّادات" desc="إحصاءات، قمع التحويل، المشتركون، الزوار، عدّادات الواجهة" icon={Users} defaultOpen={false}>
      <AdminFunnel />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Eye} label="الزوار (المعروض)" value={data.displayed_visitors.toLocaleString("en-US")} color="#734F96" testid="stat-visitors" />
        <Card icon={Users} label="المستخدمون (المعروض)" value={data.displayed_users.toLocaleString("en-US")} color="#D89134" testid="stat-users" />
        <Card icon={CreditCard} label="الاشتراكات المدفوعة" value={data.subscriptions} color="#1C4B3E" testid="stat-subs" />
        <Card icon={FolderKanban} label="المشاريع" value={data.projects} color="#1D4ED8" testid="stat-projects" />
      </div>
      <div className="glass rounded-2xl p-5">
        <div className="text-sm text-muted-foreground mb-3 flex flex-wrap gap-x-6 gap-y-1">
          <span>زيارات فعلية (إجمالي): <b className="text-foreground">{data.real_visits.toLocaleString("en-US")}</b></span>
          <span>زوّار فعليون (IP فريد): <b className="text-foreground" data-testid="unique-visitors">{Number(data.unique_visitors || 0).toLocaleString("en-US")}</b></span>
          <span>مستخدمون مسجّلون: <b className="text-foreground">{data.total_users}</b></span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(data.plan_breakdown || {}).map(([plan, n]) => (
            <div key={plan} className="flex items-center justify-between text-sm border border-border rounded-xl px-4 py-2" data-testid={`plan-${plan}`}>
              <span className="text-muted-foreground">باقة {plan}</span><span className="font-bold">{n}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Visitors by IP */}
      <div className="glass rounded-2xl p-5" data-testid="visitors-section">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-accent" />
          <h2 className="font-bold">الزوّار حسب الـ IP (آخر 30)</h2>
        </div>
        {(data.recent_visitors || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">لا توجد زيارات مسجّلة بعد.</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground text-xs border-b border-border">
                <th className="text-start py-2 font-medium">IP</th>
                <th className="text-start py-2 font-medium">عدد الزيارات</th>
                <th className="text-start py-2 font-medium">آخر زيارة</th>
              </tr></thead>
              <tbody>
                {data.recent_visitors.map((v, i) => (
                  <tr key={i} className="border-b border-border/50" data-testid={`visitor-${i}`}>
                    <td className="py-2" dir="ltr">{v.ip}</td>
                    <td className="py-2 font-bold">{v.count}</td>
                    <td className="py-2 text-muted-foreground text-xs" dir="ltr">{v.last_seen ? String(v.last_seen).slice(0, 16).replace("T", " ") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="glass rounded-2xl p-6 max-w-xl">
        <h2 className="text-lg font-bold mb-1">التحكم في عدّادات الواجهة</h2>
        <p className="text-xs text-muted-foreground mb-4">تُضاف هذه الأرقام إلى الأعداد الحقيقية عند عرضها للزوار على الصفحة الرئيسية (لإظهار نشاط المنصة).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">رصيد الزوار الأساسي</Label>
            <Input type="number" min="0" value={vb} onChange={(e) => setVb(e.target.value)} className="mt-1" data-testid="visitors-base-input" />
          </div>
          <div>
            <Label className="text-sm">رصيد المستخدمين الأساسي</Label>
            <Input type="number" min="0" value={ub} onChange={(e) => setUb(e.target.value)} className="mt-1" data-testid="users-base-input" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="mt-4 gap-2" data-testid="save-bases-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ
        </Button>
      </div>
      <AdminCustomers leads={data.leads || []} onChanged={load} />
      </AdminSection>

      <AdminSection id="finance" title="المالية والرصيد والتكاليف" desc="الرصيد، الفواتير، الكوبونات، استهلاك الذكاء الاصطناعي، معمل التكلفة" icon={Wallet} defaultOpen={false}>
      <AdminCredits />
      <VatSettingsCard />
      <AdminFinance />
      {/* Coupons management */}
      <div className="glass rounded-2xl p-6" data-testid="coupons-admin">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-bold">كوبونات الخصم</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-5">أنشئ كوبونات خصم (مثل كوبون <b className="text-accent">FREEMONTH</b> بخصم 100% لمدة شهر) يستخدمها العملاء عند الاشتراك. فعّل خانة «بانر» لإظهار الكوبون في البانر الترويجي بالصفحة الرئيسية. جدّد التاريخ من هنا لإبقاء العرض سارياً.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end mb-5">
          <div>
            <Label className="text-sm">الكود</Label>
            <Input value={cCode} onChange={(e) => setCCode(e.target.value.toUpperCase())} placeholder="FREEMONTH" dir="ltr"
              className="mt-1 text-start" data-testid="coupon-code-input" />
          </div>
          <div>
            <Label className="text-sm">نسبة الخصم %</Label>
            <Input type="number" min="1" max="100" value={cPct} onChange={(e) => setCPct(e.target.value)} className="mt-1" data-testid="coupon-pct-input" />
          </div>
          <div>
            <Label className="text-sm">حد الاستخدام (اختياري)</Label>
            <Input type="number" min="1" value={cMax} onChange={(e) => setCMax(e.target.value)} placeholder="غير محدود" className="mt-1" data-testid="coupon-max-input" />
          </div>
          <div>
            <Label className="text-sm">تاريخ الانتهاء (اختياري)</Label>
            <Input type="date" value={cExp} onChange={(e) => setCExp(e.target.value)} className="mt-1" dir="ltr" data-testid="coupon-exp-input" />
          </div>
        </div>
        <Button onClick={createCoupon} disabled={cSaving} className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="create-coupon-btn">
          {cSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} إنشاء كوبون
        </Button>

        {coupons.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground text-xs border-b border-border">
                <th className="text-start py-2 font-medium">الكود</th>
                <th className="text-start py-2 font-medium">الخصم</th>
                <th className="text-start py-2 font-medium">الاستخدام</th>
                <th className="text-start py-2 font-medium">الانتهاء</th>
                <th className="text-start py-2 font-medium">بانر</th>
                <th className="text-start py-2 font-medium">مفعّل</th>
                <th className="text-start py-2 font-medium"></th>
              </tr></thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className="border-b border-border/50" data-testid={`coupon-row-${c.code}`}>
                    <td className="py-2 font-bold" dir="ltr">{c.code}</td>
                    <td className="py-2">{c.discount_percent}%</td>
                    <td className="py-2">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1">
                        <Input type="date" defaultValue={c.expires_at ? String(c.expires_at).slice(0, 10) : ""}
                          onBlur={(e) => { if (e.target.value !== (c.expires_at ? String(c.expires_at).slice(0,10) : "")) setCouponExpiry(c, e.target.value); }}
                          className="h-8 w-[140px] text-xs" dir="ltr" data-testid={`coupon-expiry-${c.code}`} />
                        <Button size="sm" variant="outline" className="h-8 gap-1 border-accent/40 text-accent whitespace-nowrap"
                          onClick={() => renewCoupon(c, 30)} data-testid={`renew-coupon-${c.code}`}>
                          <RefreshCw className="w-3.5 h-3.5" /> +30
                        </Button>
                      </div>
                    </td>
                    <td className="py-2"><Switch checked={!!c.promo} onCheckedChange={() => togglePromo(c)} data-testid={`promo-coupon-${c.code}`} /></td>
                    <td className="py-2"><Switch checked={!!c.active} onCheckedChange={() => toggleCoupon(c)} data-testid={`toggle-coupon-${c.code}`} /></td>
                    <td className="py-2">
                      <Button size="sm" variant="outline" className="gap-1 border-destructive/40 text-destructive hover:bg-destructive/10"
                        onClick={() => deleteCoupon(c)} data-testid={`delete-coupon-${c.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* AI consumption / Runway cost report */}
      <div className="glass rounded-2xl p-6" data-testid="usage-admin">
        <div className="flex items-center gap-2 mb-1">
          <Coins className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-bold">استهلاك وتكلفة الذكاء الاصطناعي (Runway)</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-5">التكلفة الفعلية التي ندفعها لـ Runway مقابل التوليد، وفق أسعار الكريدت الرسمية (الكريدت = ${usage?.rates?.credit_usd ?? 0.01}): صورة gen4_image 720p = {usage?.rates?.image_credits} كريدت (${usage?.rates?.image_usd})، وثانية فيديو gen4_turbo = {usage?.rates?.video_credits_per_sec} كريدت (${usage?.rates?.video_usd_per_sec}) + إطار أساسي.</p>
        {!usage ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <StatBox label="صور مولّدة" value={usage.images} />
              <StatBox label="فيديوهات مولّدة" value={`${usage.videos} (${usage.video_seconds}ث)`} />
              <StatBox label="كريدت Runway" value={usage.credits} />
              <StatBox label="التكلفة (USD)" value={`$${usage.cost_usd}`} accent />
              <StatBox label="التكلفة (ريال)" value={`${usage.cost_sar} ر.س`} accent />
            </div>
            {usage.daily.length > 0 && (
              <div className="overflow-x-auto mb-6">
                <h3 className="font-bold text-sm mb-2">آخر 30 يوم</h3>
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b border-border">
                    <th className="text-start py-2 font-medium">اليوم</th>
                    <th className="text-start py-2 font-medium">صور</th>
                    <th className="text-start py-2 font-medium">فيديو</th>
                    <th className="text-start py-2 font-medium">التكلفة (USD)</th>
                  </tr></thead>
                  <tbody>
                    {usage.daily.map((d, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-2" dir="ltr">{d.date}</td>
                        <td className="py-2">{d.images}</td>
                        <td className="py-2">{d.videos}</td>
                        <td className="py-2 text-accent">${d.cost_usd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {usage.top_users.length > 0 && (
              <div className="overflow-x-auto">
                <h3 className="font-bold text-sm mb-2">أعلى المستخدمين استهلاكاً</h3>
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b border-border">
                    <th className="text-start py-2 font-medium">المستخدم</th>
                    <th className="text-start py-2 font-medium">صور</th>
                    <th className="text-start py-2 font-medium">فيديو</th>
                    <th className="text-start py-2 font-medium">التكلفة (USD)</th>
                  </tr></thead>
                  <tbody>
                    {usage.top_users.map((u, i) => (
                      <tr key={i} className="border-b border-border/50" data-testid={`usage-user-${i}`}>
                        <td className="py-2" dir="ltr">{u.email}</td>
                        <td className="py-2">{u.images}</td>
                        <td className="py-2">{u.videos}</td>
                        <td className="py-2 text-accent">${u.cost_usd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      <AdminCostLab />
      </AdminSection>

      <AdminSection id="docs" title="الوثائق والأدلة" desc="دراسة وضع المنصة (تُحدَّث تلقائيًا) + أدلة التفعيل والاعتمادات خطوة بخطوة (Meta: سوبر رواج والسكرتير التنفيذي)" icon={BookOpen} defaultOpen={false}>
      <AdminStudy />
      </AdminSection>

      <AdminSection id="feedback" title="التغذية الراجعة والتقييمات" desc="رسائل العملاء ومراجعة التقييمات" icon={MessageSquare} defaultOpen={false}>
      {/* Feedback inbox */}
      <div className="glass rounded-2xl p-6" data-testid="feedback-admin">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-bold">صندوق المقترحات والشكاوى ({feedback.length})</h2>
        </div>
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">لا توجد رسائل بعد.</p>
        ) : (
          <div className="space-y-3">
            {feedback.map((f) => (
              <div key={f.id} className="border border-border rounded-xl p-4" data-testid={`feedback-${f.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${f.kind === "complaint" ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`}>{KIND_LABEL[f.kind] || f.kind}</span>
                    <span className="text-sm font-bold">{f.name}</span>
                    <span className="text-xs text-muted-foreground" dir="ltr">{f.email}</span>
                    {f.status === "replied" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">تم الرد</span>}
                  </div>
                  <button onClick={() => deleteFeedback(f)} className="text-destructive/70 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="text-sm text-foreground/85 mb-3 leading-relaxed">{f.message}</p>
                {f.reply ? (
                  <div className="bg-emerald-500/5 border-r-2 border-emerald-500 rounded px-3 py-2 text-sm text-muted-foreground">
                    <span className="font-bold text-emerald-600">ردّك: </span>{f.reply}
                  </div>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    <Textarea value={replyText[f.id] || ""} onChange={(e) => setReplyText((p) => ({ ...p, [f.id]: e.target.value }))}
                      rows={2} placeholder="اكتب ردّك ليصل لبريد العميل..." className="bg-secondary border-border flex-1 min-w-[220px]" data-testid={`reply-text-${f.id}`} />
                    <Button onClick={() => sendReply(f)} disabled={replying === f.id} className="gap-1 bg-primary hover:bg-primary/90 text-white self-end" data-testid={`reply-send-${f.id}`}>
                      {replying === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} رد عبر الإيميل
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ProductRatingsAdmin />
      {/* Reviews moderation */}
      <div className="glass rounded-2xl p-6" data-testid="reviews-admin">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-bold">تقييمات العملاء ({reviews.length})</h2>
        </div>
        {reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">لا توجد تقييمات بعد.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {reviews.map((r) => (
              <div key={r.id} className="border border-border rounded-xl p-4" data-testid={`review-admin-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex gap-0.5 mb-1" dir="ltr">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />)}
                  </div>
                  <button onClick={() => deleteReview(r)} className="text-destructive/70 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                {r.text && <p className="text-sm text-foreground/85 my-1">{r.text}</p>}
                <div className="text-xs text-muted-foreground">— {r.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      </AdminSection>

      <AdminSection id="ops" title="التشغيل والحمل" desc="مراقبة الطوابير والحمل" icon={Activity} defaultOpen={false}>
      <AdminLoad />
      </AdminSection>
    </div>
  );
}
